/**
 * Product Add-on System
 * Handles adding add-on products to cart when main product is added
 * Also monitors cart and removes add-ons when their parent products are removed
 * Works independently without interfering with theme or other apps
 */
(function() {
  'use strict';

  // Storage key for tracking parent-addon relationships
  const ADDON_STORAGE_KEY = 'runnies_addon_relationships';
  
  // How often to poll cart when drawer is open (ms)
  const CART_POLL_INTERVAL = 2000;

  class ProductAddon {
    constructor() {
      this.addonCheckboxes = [];
      this.isProcessing = false;
      this.cartPollTimer = null;
      this.lastCartItemCount = null;
      this.observer = null;
      this.init();
    }

    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    setup() {
      // Find all add-on checkboxes
      this.addonCheckboxes = document.querySelectorAll('[data-addon-variant-id]');
      
      if (this.addonCheckboxes.length === 0) {
        // No add-ons on this page, but still set up cart monitoring
        this.setupCartMonitoring();
        return;
      }

      console.log(`[Product Addon] Found ${this.addonCheckboxes.length} add-on(s)`);

      // Find product form (handle multiple form selectors)
      const productForm = this.findProductForm();
      
      if (!productForm) {
        console.warn('[Product Addon] Product form not found');
        return;
      }

      // Intercept form submission
      productForm.addEventListener('submit', (e) => this.handleFormSubmit(e), true);
      
      // Also listen for AJAX cart events if theme uses them
      document.addEventListener('submit', (e) => {
        if (e.target.closest('product-form') || e.target.closest('[action*="/cart/add"]')) {
          this.handleFormSubmit(e);
        }
      }, true);

      // Listen for variant changes to show/hide add-on based on restrictions
      this.setupVariantRestrictions();

      // Set up cart monitoring for orphan cleanup
      this.setupCartMonitoring();

      console.log('[Product Addon] Initialized successfully');
    }

    setupCartMonitoring() {
      console.log('[Product Addon] Setting up cart monitoring');

      // 1. Listen for theme-specific cart events
      const cartEvents = [
        'dispatch:cart-drawer:refresh',
        'dispatch:cart-drawer:open',
        'dispatch:cart-drawer:close',
        'cart:updated',
        'cart:refresh',
        'cart:change',
        'theme:cart:update',
        'ajaxCart:itemAdded',
        'ajaxCart:updated'
      ];

      cartEvents.forEach(eventName => {
        document.addEventListener(eventName, () => {
          console.log(`[Product Addon] Cart event detected: ${eventName}`);
          setTimeout(() => this.cleanupOrphanedAddons(), 300);
        });
      });

      // 2. Set up MutationObserver to watch cart drawer/form changes
      this.setupCartObserver();

      // 3. Listen for clicks on remove buttons anywhere
      document.addEventListener('click', (e) => {
        const removeButton = e.target.closest('[data-cart-remove], .cart-item__remove, .cart__remove, [name="remove"], .remove-item, .cart-drawer__remove, button[aria-label*="Remove"]');
        if (removeButton) {
          console.log('[Product Addon] Remove button clicked');
          // Check after a delay to let the cart update
          setTimeout(() => this.cleanupOrphanedAddons(), 500);
          setTimeout(() => this.cleanupOrphanedAddons(), 1500);
        }
      });

      // 4. Listen for quantity changes to zero (removal)
      document.addEventListener('change', (e) => {
        const quantityInput = e.target.closest('[name="updates[]"], [name="quantity"], .cart-item__quantity input, .cart__quantity input');
        if (quantityInput && (quantityInput.value === '0' || quantityInput.value === '')) {
          console.log('[Product Addon] Quantity changed to 0');
          setTimeout(() => this.cleanupOrphanedAddons(), 500);
        }
      });

      // 5. Start polling when cart drawer opens
      this.setupCartDrawerPolling();

      // 6. Also poll on the cart page
      if (window.location.pathname === '/cart') {
        this.startCartPolling();
      }

      // Initial cleanup check
      setTimeout(() => this.cleanupOrphanedAddons(), 1000);
    }

    setupCartObserver() {
      // Watch for cart drawer opening/closing and content changes
      const targetNodes = [
        document.querySelector('cart-drawer'),
        document.querySelector('[data-cart-drawer]'),
        document.querySelector('.cart-drawer'),
        document.getElementById('cart-drawer'),
        document.querySelector('.mini-cart'),
        document.querySelector('[data-ajax-cart]'),
        document.body // Fallback to body for any cart-related changes
      ].filter(Boolean);

      if (targetNodes.length === 0) {
        console.log('[Product Addon] No cart container found for observer');
        return;
      }

      const config = { 
        childList: true, 
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-hidden', 'open']
      };

      this.observer = new MutationObserver((mutations) => {
        let shouldCheck = false;
        
        for (const mutation of mutations) {
          // Check if cart drawer is being shown/hidden
          if (mutation.type === 'attributes') {
            const target = mutation.target;
            if (target.classList.contains('cart-drawer') || 
                target.classList.contains('is-open') ||
                target.hasAttribute('open')) {
              shouldCheck = true;
              // Start/stop polling based on visibility
              if (this.isCartDrawerOpen()) {
                this.startCartPolling();
              } else {
                this.stopCartPolling();
              }
            }
          }
          
          // Check if cart items are being modified
          if (mutation.type === 'childList') {
            const hasCartRelatedChange = mutation.removedNodes.length > 0 && 
              Array.from(mutation.removedNodes).some(node => 
                node.nodeType === 1 && 
                (node.classList?.contains('cart-item') || 
                 node.classList?.contains('cart__item') ||
                 node.querySelector?.('[data-cart-item]'))
              );
            if (hasCartRelatedChange) {
              shouldCheck = true;
            }
          }
        }

        if (shouldCheck) {
          console.log('[Product Addon] Cart DOM change detected');
          setTimeout(() => this.cleanupOrphanedAddons(), 300);
        }
      });

      targetNodes.forEach(node => {
        try {
          this.observer.observe(node, config);
        } catch (e) {
          // Ignore observation errors
        }
      });

      console.log(`[Product Addon] Observing ${targetNodes.length} cart containers`);
    }

    setupCartDrawerPolling() {
      // Watch for cart drawer visibility changes
      const checkDrawerVisibility = () => {
        const isOpen = this.isCartDrawerOpen();
        
        if (isOpen && !this.cartPollTimer) {
          this.startCartPolling();
        } else if (!isOpen && this.cartPollTimer) {
          this.stopCartPolling();
        }
      };

      // Check periodically for cart drawer state
      setInterval(checkDrawerVisibility, 1000);
    }

    isCartDrawerOpen() {
      const drawer = document.querySelector('cart-drawer, .cart-drawer, [data-cart-drawer]');
      if (!drawer) return false;
      
      // Check various ways the drawer might be open
      return drawer.hasAttribute('open') ||
             drawer.classList.contains('is-open') ||
             drawer.classList.contains('active') ||
             drawer.classList.contains('visible') ||
             drawer.getAttribute('aria-hidden') === 'false' ||
             window.getComputedStyle(drawer).display !== 'none';
    }

    startCartPolling() {
      if (this.cartPollTimer) return; // Already polling
      
      console.log('[Product Addon] Starting cart polling');
      
      this.cartPollTimer = setInterval(async () => {
        try {
          const cart = await this.fetchCart();
          const currentCount = cart.item_count;
          
          // If item count decreased, check for orphans
          if (this.lastCartItemCount !== null && currentCount < this.lastCartItemCount) {
            console.log('[Product Addon] Cart item count decreased:', this.lastCartItemCount, '->', currentCount);
            await this.cleanupOrphanedAddons();
          }
          
          this.lastCartItemCount = currentCount;
        } catch (e) {
          // Ignore fetch errors during polling
        }
      }, CART_POLL_INTERVAL);
    }

    stopCartPolling() {
      if (this.cartPollTimer) {
        console.log('[Product Addon] Stopping cart polling');
        clearInterval(this.cartPollTimer);
        this.cartPollTimer = null;
      }
    }

    async fetchCart() {
      const response = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      return response.json();
    }

    // Get addon relationships from localStorage
    getAddonRelationships() {
      try {
        const data = localStorage.getItem(ADDON_STORAGE_KEY);
        return data ? JSON.parse(data) : {};
      } catch (e) {
        return {};
      }
    }

    // Save addon relationship to localStorage
    saveAddonRelationship(parentVariantId, addonVariantId) {
      try {
        const relationships = this.getAddonRelationships();
        if (!relationships[parentVariantId]) {
          relationships[parentVariantId] = [];
        }
        if (!relationships[parentVariantId].includes(addonVariantId)) {
          relationships[parentVariantId].push(addonVariantId);
        }
        localStorage.setItem(ADDON_STORAGE_KEY, JSON.stringify(relationships));
        console.log('[Product Addon] Saved relationship:', parentVariantId, '->', addonVariantId);
      } catch (e) {
        console.error('[Product Addon] Failed to save relationship:', e);
      }
    }

    // Remove addon relationship from localStorage
    removeAddonRelationship(parentVariantId, addonVariantId = null) {
      try {
        const relationships = this.getAddonRelationships();
        if (addonVariantId) {
          // Remove specific addon
          if (relationships[parentVariantId]) {
            relationships[parentVariantId] = relationships[parentVariantId].filter(id => id !== addonVariantId);
            if (relationships[parentVariantId].length === 0) {
              delete relationships[parentVariantId];
            }
          }
        } else {
          // Remove all addons for this parent
          delete relationships[parentVariantId];
        }
        localStorage.setItem(ADDON_STORAGE_KEY, JSON.stringify(relationships));
      } catch (e) {
        console.error('[Product Addon] Failed to remove relationship:', e);
      }
    }

    // Clean up orphaned add-ons (add-ons whose parent products are no longer in cart)
    async cleanupOrphanedAddons() {
      try {
        // Fetch current cart
        const cart = await this.fetchCart();
        
        const cartVariantIds = cart.items.map(item => String(item.variant_id));
        const relationships = this.getAddonRelationships();
        
        console.log('[Product Addon] Checking for orphaned add-ons:', {
          cartItems: cart.items.length,
          cartVariantIds: cartVariantIds,
          relationships: relationships
        });

        const addonsToRemove = [];
        const relationshipsToClean = [];

        // Check each relationship
        for (const [parentId, addonIds] of Object.entries(relationships)) {
          const parentInCart = cartVariantIds.includes(parentId);
          
          if (!parentInCart) {
            // Parent not in cart - check if addons are still there and need removal
            for (const addonId of addonIds) {
              if (cartVariantIds.includes(addonId)) {
                addonsToRemove.push(addonId);
                console.log('[Product Addon] Found orphaned add-on:', addonId, 'from parent:', parentId);
              }
            }
            relationshipsToClean.push(parentId);
          }
        }

        // Clean up relationships for parents no longer in cart
        for (const parentId of relationshipsToClean) {
          this.removeAddonRelationship(parentId);
        }

        // Remove orphaned add-ons from cart
        if (addonsToRemove.length > 0) {
          console.log('[Product Addon] Removing orphaned add-ons:', addonsToRemove);
          await this.removeItemsFromCart(addonsToRemove, cart);
        }

      } catch (error) {
        console.error('[Product Addon] Cleanup error:', error);
      }
    }

    // Remove items from cart by variant ID
    async removeItemsFromCart(variantIds, cart) {
      try {
        // Build updates object to set quantities to 0
        const updates = {};
        
        for (const item of cart.items) {
          if (variantIds.includes(String(item.variant_id))) {
            // Use line item key for more precise removal
            updates[item.key] = 0;
          }
        }

        if (Object.keys(updates).length === 0) {
          return;
        }

        console.log('[Product Addon] Removing items:', updates);

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates })
        });

        if (response.ok) {
          console.log('[Product Addon] Successfully removed orphaned add-ons');
          
          // Trigger cart refresh
          this.triggerCartRefresh();
        }

      } catch (error) {
        console.error('[Product Addon] Failed to remove items:', error);
      }
    }

    triggerCartRefresh() {
      // Try multiple refresh methods
      const refreshEvents = [
        'dispatch:cart-drawer:refresh',
        'cart:updated',
        'cart:refresh'
      ];
      
      refreshEvents.forEach(eventName => {
        document.dispatchEvent(new CustomEvent(eventName, { bubbles: true }));
      });

      // Also try theme-specific refresh
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.refresh === 'function') {
        cartDrawer.refresh();
      }

      // Force a re-render of cart section if possible
      const cartForm = document.querySelector('cart-form');
      if (cartForm) {
        const sectionId = cartForm.dataset.sectionId;
        if (sectionId) {
          fetch(`/cart?section_id=${sectionId}`)
            .then(res => res.text())
            .then(html => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const newContent = doc.querySelector('cart-form');
              if (newContent && cartForm.parentNode) {
                cartForm.innerHTML = newContent.innerHTML;
              }
            })
            .catch(() => {});
        }
      }
    }

    setupVariantRestrictions() {
      const addonContainer = document.querySelector('.product-addon-container[data-variant-restrictions]');
      if (!addonContainer) return;

      const restrictions = addonContainer.dataset.variantRestrictions;
      if (!restrictions) return;

      const allowedVariants = restrictions.split(',').map(v => v.trim().toLowerCase());
      console.log('[Product Addon] Setting up variant restrictions:', allowedVariants);
      
      // Check initial variant
      this.checkVariantRestriction(addonContainer, allowedVariants);

      // Listen for variant changes - multiple event types
      const checkVariant = () => {
        console.log('[Product Addon] Variant change detected, checking restrictions...');
        // Check immediately and after a short delay (for themes that update asynchronously)
        this.checkVariantRestriction(addonContainer, allowedVariants);
        setTimeout(() => this.checkVariantRestriction(addonContainer, allowedVariants), 50);
        setTimeout(() => this.checkVariantRestriction(addonContainer, allowedVariants), 150);
        setTimeout(() => this.checkVariantRestriction(addonContainer, allowedVariants), 300);
      };

      // Theme-specific variant change events
      document.addEventListener('on:variant:change', (e) => {
        console.log('[Product Addon] on:variant:change event');
        this.checkVariantRestriction(addonContainer, allowedVariants, e.detail?.variant);
      });

      document.addEventListener('variant:change', checkVariant);
      
      // Listen for change events on variant selectors
      document.addEventListener('change', (e) => {
        if (e.target.closest('.option-selector, variant-picker, [data-option-selector]')) {
          checkVariant();
        }
      });

      // Listen for input changes in option selectors
      const optionSelectors = document.querySelectorAll('.option-selector input, [data-option-selector] input, .option-selector select, [data-option-selector] select');
      optionSelectors.forEach(selector => {
        selector.addEventListener('change', checkVariant);
        selector.addEventListener('click', checkVariant);
      });

      // ENHANCED: Listen for clicks on variant buttons (theme-specific)
      // This catches button-style variant selectors like .opt-label--btn
      document.addEventListener('click', (e) => {
        const variantButton = e.target.closest('.opt-label--btn, .option-label, [data-option-value], .variant-option, .swatch, .color-swatch, label[for*="option"], .product-option button, .product-option label');
        if (variantButton) {
          console.log('[Product Addon] Variant button clicked:', variantButton.textContent?.trim());
          checkVariant();
        }
      });

      // ENHANCED: Watch for class changes on variant buttons (some themes use is-selected, active, etc.)
      this.setupVariantButtonObserver(addonContainer, allowedVariants, checkVariant);

      // ENHANCED: Watch for URL changes (some themes update URL with variant ID)
      let lastUrl = window.location.href;
      const urlObserver = new MutationObserver(() => {
        if (window.location.href !== lastUrl) {
          lastUrl = window.location.href;
          console.log('[Product Addon] URL changed, checking variant');
          checkVariant();
        }
      });
      // Watch for history state changes
      window.addEventListener('popstate', checkVariant);
      
      // Also poll for URL changes (fallback for pushState)
      setInterval(() => {
        if (window.location.href !== lastUrl) {
          lastUrl = window.location.href;
          console.log('[Product Addon] URL changed (poll), checking variant');
          checkVariant();
        }
      }, 500);

      // ENHANCED: Watch for hidden input changes (variant ID input)
      const variantIdInput = document.querySelector('input[name="id"], select[name="id"]');
      if (variantIdInput) {
        // Use MutationObserver to watch for value changes
        const inputObserver = new MutationObserver(() => {
          console.log('[Product Addon] Variant ID input changed');
          checkVariant();
        });
        inputObserver.observe(variantIdInput, { attributes: true, attributeFilter: ['value'] });
        
        // Also listen for input/change events
        variantIdInput.addEventListener('change', checkVariant);
        variantIdInput.addEventListener('input', checkVariant);
      }
    }

    setupVariantButtonObserver(addonContainer, allowedVariants, checkVariant) {
      // Find variant picker containers
      const variantContainers = document.querySelectorAll('variant-picker, .option-selector, [data-option-selector], .product-options, .variant-selector');
      
      if (variantContainers.length === 0) return;

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          // Check if class changed (is-selected, active, checked, etc.)
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            if (target.classList.contains('is-selected') || 
                target.classList.contains('active') || 
                target.classList.contains('selected') ||
                target.classList.contains('checked')) {
              console.log('[Product Addon] Variant button class changed to selected');
              checkVariant();
            }
          }
          // Check if aria-checked changed
          if (mutation.type === 'attributes' && mutation.attributeName === 'aria-checked') {
            if (mutation.target.getAttribute('aria-checked') === 'true') {
              console.log('[Product Addon] Variant aria-checked changed');
              checkVariant();
            }
          }
          // Check if checked attribute changed on inputs
          if (mutation.type === 'attributes' && mutation.attributeName === 'checked') {
            console.log('[Product Addon] Input checked attribute changed');
            checkVariant();
          }
        }
      });

      variantContainers.forEach(container => {
        observer.observe(container, {
          attributes: true,
          attributeFilter: ['class', 'aria-checked', 'checked', 'aria-selected'],
          subtree: true
        });
      });

      console.log(`[Product Addon] Observing ${variantContainers.length} variant containers for changes`);
    }

    checkVariantRestriction(addonContainer, allowedVariants, variant = null) {
      let selectedOptions = [];

      // Method 1: Try variant picker component
      const variantPicker = document.querySelector('variant-picker');
      if (variantPicker) {
        if (typeof variantPicker.getSelectedOptions === 'function') {
          selectedOptions = variantPicker.getSelectedOptions();
        } else {
          // Read from option selectors
          const optionSelectors = variantPicker.querySelectorAll('.option-selector');
          optionSelectors.forEach(selector => {
            if (selector.dataset.selectorType === 'dropdown') {
              const selected = selector.querySelector('.custom-select__option[aria-selected="true"], select option:checked');
              if (selected) {
                const value = selected.dataset.value || selected.value || selected.textContent.trim();
                if (value) selectedOptions.push(value);
              }
            } else {
              const selected = selector.querySelector('input:checked, input[type="radio"]:checked');
              if (selected && selected.value) {
                selectedOptions.push(selected.value);
              }
            }
          });
        }
      }

      // Method 2: Try reading from checked inputs/selects directly
      if (selectedOptions.length === 0) {
        const checkedInputs = document.querySelectorAll('.option-selector input:checked, [data-option-selector] input:checked');
        checkedInputs.forEach(input => {
          if (input.value) selectedOptions.push(input.value);
        });
        
        const selectedSelects = document.querySelectorAll('.option-selector select, [data-option-selector] select');
        selectedSelects.forEach(select => {
          if (select.value && select.value !== '') {
            selectedOptions.push(select.value);
          }
        });
      }

      // Method 3: Try reading from variant data attributes
      if (selectedOptions.length === 0 && variant) {
        if (variant.option1) selectedOptions.push(variant.option1);
        if (variant.option2) selectedOptions.push(variant.option2);
        if (variant.option3) selectedOptions.push(variant.option3);
      }

      // Method 4: Try reading from product form data
      if (selectedOptions.length === 0) {
        const productForm = this.findProductForm();
        if (productForm) {
          const formData = new FormData(productForm);
          const option1 = formData.get('option1') || formData.get('options[Option 1]');
          const option2 = formData.get('option2') || formData.get('options[Option 2]');
          const option3 = formData.get('option3') || formData.get('options[Option 3]');
          if (option1) selectedOptions.push(option1);
          if (option2) selectedOptions.push(option2);
          if (option3) selectedOptions.push(option3);
        }
      }

      // Method 5: ENHANCED - Try reading from button with is-selected class
      if (selectedOptions.length === 0) {
        const selectedButtons = document.querySelectorAll('.opt-label--btn.is-selected, .option-label.is-selected, .variant-option.is-selected, .swatch.is-selected, [data-option-value].is-selected, label.is-selected');
        selectedButtons.forEach(btn => {
          // Try to get value from various attributes
          const value = btn.dataset.optionValue || 
                       btn.dataset.value || 
                       btn.getAttribute('data-option-value') ||
                       btn.textContent?.trim();
          if (value) selectedOptions.push(value);
        });
      }

      // Method 6: ENHANCED - Try reading from label that follows checked input
      if (selectedOptions.length === 0) {
        const checkedRadios = document.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
        checkedRadios.forEach(input => {
          // Get associated label
          const label = document.querySelector(`label[for="${input.id}"]`);
          if (label) {
            const value = label.dataset.optionValue || label.textContent?.trim();
            if (value) selectedOptions.push(value);
          }
          // Also try input value
          if (input.value) selectedOptions.push(input.value);
        });
      }

      // Method 7: ENHANCED - Check URL for variant parameter
      if (selectedOptions.length === 0) {
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');
        if (variantId) {
          // Try to find the variant title from the page
          const variantJson = document.querySelector('[data-product-json], [type="application/json"][data-product-variants]');
          if (variantJson) {
            try {
              const productData = JSON.parse(variantJson.textContent);
              const variants = productData.variants || productData;
              const foundVariant = variants.find(v => String(v.id) === variantId);
              if (foundVariant) {
                if (foundVariant.option1) selectedOptions.push(foundVariant.option1);
                if (foundVariant.option2) selectedOptions.push(foundVariant.option2);
                if (foundVariant.option3) selectedOptions.push(foundVariant.option3);
              }
            } catch (e) {}
          }
        }
      }

      // Method 8: ENHANCED - Read from page title or variant title display
      if (selectedOptions.length === 0) {
        const variantTitle = document.querySelector('.product__variant-title, .selected-variant, [data-variant-title]');
        if (variantTitle && variantTitle.textContent) {
          const value = variantTitle.textContent.trim();
          if (value && value !== '') {
            selectedOptions.push(value);
          }
        }
      }

      console.log('[Product Addon] Selected options detected:', selectedOptions);

      if (selectedOptions.length === 0) {
        // If we can't get options, show add-on (fallback)
        console.log('[Product Addon] No options detected, showing add-on (fallback)');
        addonContainer.style.display = '';
        return;
      }

      // Check if any of the selected option values match restrictions
      const selectedOptionsLower = selectedOptions.map(opt => String(opt).toLowerCase().trim());
      const matches = selectedOptionsLower.some(opt => allowedVariants.includes(opt));

      console.log('[Product Addon] Checking restrictions:', {
        selectedOptions: selectedOptionsLower,
        allowedVariants: allowedVariants,
        matches: matches
      });

      if (matches) {
        console.log('[Product Addon] Variant matches - SHOWING add-on');
        addonContainer.style.display = '';
        addonContainer.style.opacity = '1';
      } else {
        console.log('[Product Addon] Variant does not match - HIDING add-on');
        addonContainer.style.display = 'none';
      }
    }

    findProductForm() {
      // Try multiple selectors to find the product form
      const selectors = [
        'form[action*="/cart/add"]',
        'product-form form',
        '[data-product-form]',
        'form.product-form'
      ];

      for (const selector of selectors) {
        const form = document.querySelector(selector);
        if (form) return form;
      }

      return null;
    }

    async handleFormSubmit(e) {
      // Get checked add-ons
      const checkedAddons = Array.from(this.addonCheckboxes)
        .filter(cb => cb.checked)
        .map(cb => ({
          variantId: cb.dataset.addonVariantId,
          quantity: parseInt(cb.dataset.addonQuantity || '1'),
          name: cb.dataset.addonName || 'Add-on'
        }));

      if (checkedAddons.length === 0) {
        return; // No add-ons selected, let form submit normally
      }

      // Prevent default form submission
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (this.isProcessing) {
        console.warn('[Product Addon] Already processing, ignoring duplicate submit');
        return;
      }

      this.isProcessing = true;

      try {
        // Get main product data from form
        const formData = new FormData(e.target);
        const mainVariantId = formData.get('id');
        const mainQuantity = parseInt(formData.get('quantity') || '1');

        if (!mainVariantId) {
          throw new Error('Main product variant ID not found');
        }

        console.log('[Product Addon] Adding main product:', mainVariantId);
        console.log('[Product Addon] Adding add-ons:', checkedAddons);

        // Prepare all items to add
        const itemsToAdd = [
          { id: mainVariantId, quantity: mainQuantity }
        ];

        // Add checked add-ons and save relationships
        checkedAddons.forEach(addon => {
          itemsToAdd.push({
            id: addon.variantId,
            quantity: addon.quantity
          });
          // Save the relationship for cleanup later
          this.saveAddonRelationship(String(mainVariantId), String(addon.variantId));
        });

        // Add all items to cart using bulk API
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items: itemsToAdd })
        });

        const data = await response.json();

        if (!response.ok || data.status === 422) {
          throw new Error(data.description || data.message || 'Failed to add items to cart');
        }

        console.log('[Product Addon] Successfully added all items:', data);

        // Trigger cart refresh events
        this.refreshCart();

        // Show success feedback
        this.showSuccessFeedback(checkedAddons.length);

      } catch (error) {
        console.error('[Product Addon] Error:', error);
        this.showErrorFeedback(error.message);
        this.isProcessing = false;
      }
    }

    async refreshCart() {
      try {
        console.log('[Product Addon] Refreshing cart...');

        // Get cart drawer section ID (needed for theme's refresh mechanism)
        const cartDrawer = document.querySelector('cart-drawer');
        const cartForm = cartDrawer ? cartDrawer.querySelector('cart-form') : null;
        const sectionId = cartForm ? cartForm.dataset.sectionId : null;

        if (sectionId) {
          // Use theme's proper refresh mechanism
          // Fetch the cart section HTML
          const sectionUrl = `/sections/cart-drawer?section_id=${sectionId}`;
          const sectionResponse = await fetch(sectionUrl);
          const sectionHtml = await sectionResponse.text();

          // Update cart drawer using theme's method
          if (cartDrawer && typeof cartDrawer.updateFromCartChange === 'function') {
            cartDrawer.updateFromCartChange(sectionHtml);
            console.log('[Product Addon] Cart drawer updated via updateFromCartChange');
          } else if (cartForm && typeof cartForm.refreshFromHtml === 'function') {
            cartForm.refreshFromHtml(sectionHtml);
            console.log('[Product Addon] Cart form updated via refreshFromHtml');
          } else {
            // Fallback: trigger theme's refresh event
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
              bubbles: true
            }));
            console.log('[Product Addon] Cart drawer refreshed via event');
          }
        } else {
          // Fallback: use theme's refresh event
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
            bubbles: true
          }));
          console.log('[Product Addon] Cart drawer refreshed via event (no section ID)');
        }

        // Open cart drawer after refresh
        setTimeout(() => {
          if (cartDrawer) {
            // Use theme's proper open method
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
              bubbles: true,
              detail: { opener: null }
            }));
            console.log('[Product Addon] Cart drawer opened');
          }
        }, 500);

      } catch (error) {
        console.error('[Product Addon] Cart refresh error:', error);
        
        // Fallback: try theme's refresh event
        try {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
            bubbles: true
          }));
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
              bubbles: true
            }));
          }, 300);
        } catch (fallbackError) {
          console.error('[Product Addon] Fallback refresh also failed:', fallbackError);
        }
      }

      this.isProcessing = false;
    }

    showSuccessFeedback(addonCount) {
      // Find the submit button
      const submitButton = document.querySelector('[type="submit"][form*="cart"], product-form button[type="submit"], form[action*="/cart/add"] button[type="submit"]');
      
      if (submitButton) {
        const originalText = submitButton.textContent;
        submitButton.textContent = `✓ Added${addonCount > 0 ? ` + ${addonCount} add-on${addonCount > 1 ? 's' : ''}` : ''}`;
        submitButton.disabled = true;
        submitButton.style.opacity = '0.7';

        setTimeout(() => {
          submitButton.textContent = originalText;
          submitButton.disabled = false;
          submitButton.style.opacity = '1';
        }, 2000);
      }
    }

    showErrorFeedback(message) {
      // Show error message (you can customize this)
      console.error('[Product Addon] Error:', message);
      
      // Optionally show a toast/alert
      const errorDiv = document.createElement('div');
      errorDiv.className = 'product-addon-error';
      errorDiv.textContent = `Error: ${message}`;
      errorDiv.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ff4444;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 10000;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      `;
      
      document.body.appendChild(errorDiv);
      
      setTimeout(() => {
        errorDiv.remove();
      }, 5000);
    }
  }

  // Initialize when script loads
  new ProductAddon();
})();
